// Content sourced directly from the client's "Process Safety Consulting" spreadsheet.
// Each service preserves the intro (from the spreadsheet's card graphics) and the
// detailed "Services Offered" list (from the spreadsheet cells).

export const CATEGORIES = [
  { id: "all", label: "All Services" },
  { id: "risk", label: "PHA & Risk Analysis" },
  { id: "compliance", label: "Compliance & Programs" },
  { id: "systems", label: "Engineering & Systems" },
  { id: "process-eng", label: "Process Engineering" },
  { id: "dust", label: "Dust Hazards" },
];

export const SERVICES = [
  {
    id: "pha",
    code: "PHA",
    category: "risk",
    title: "Process Hazard Analysis (PHA)",
    standard: "OSHA 29 CFR 1910.119",
    short:
      "PHA studies are the foundation for process safety and risk management programs. They help companies identify hazard scenarios for a process that could adversely affect people, property and the environment.",
    long:
      "We provide facilitation for all types of PHA studies, mentoring for your facilitators, independent reviews of completed studies, and dedicated scribing — delivering a detailed, comprehensive report for every study.",
    items: [
      { t: "Facilitation of all PHA study types", d: "Design-stage PHAs to improve safety before a process is built, initial PHAs for operating facilities, and revalidation PHAs on a 5-year cycle for compliance with OSHA 29 CFR 1910.119." },
      { t: "Mentoring & coaching your personnel", d: "An experienced facilitator assists your new facilitators in learning the ropes and gaining confidence in facilitating studies." },
      { t: "Reviewing PHAs", d: "An experienced facilitator reviews your completed PHAs and provides suggestions for improvements that are useful in revalidating PHAs and improving their quality." },
      { t: "PHA scribing", d: "Our team leaders can simultaneously facilitate and document a study, or we can provide a separate PHA scribe to handle documentation tasks." },
    ],
    note: "We utilize PHA-Pro in conducting and documenting studies (other software packages can also be used) and provide a detailed, comprehensive report documenting the study.",
  },
  {
    id: "pha-analytics",
    code: "PHA·A",
    category: "risk",
    title: "PHA Analytics",
    standard: "Data-Driven PSM Metrics",
    short:
      "PHA worksheets hold vital information on process risks, but much of it is obscured by the vast amount of data they contain. Analytics transform that data into meaningful information and enable better decisions.",
    long:
      "Because risks are not uniformly distributed across a process, analytics reveal where the highest risks lie, what drives them, and how well safeguards perform — turning raw worksheets into actionable insight.",
    items: [
      { t: "Comparison of risks within and across processes", d: "Analysis of risk distribution by hazard type (fire, explosion, toxic release) and consequence type (people, property, environment) to inform risk-reduction decisions." },
      { t: "Analysis of the sources and types of risk", d: "Insight into which human errors, equipment failures, or external events contribute most to risk, so dominant contributors receive special emphasis." },
      { t: "Management of safeguards", d: "Beyond holistic risk ranking, we factor individual safeguard characteristics — such as their position in the hierarchy of controls — into adequacy decisions." },
      { t: "Critical items analysis", d: "Identification and prioritization of critical equipment for mechanical integrity programs and critical human actions for operating, maintenance and emergency procedures." },
      { t: "Specific queries of the database", d: "Deeper analytics that probe the PHA database, posing questions at increasing levels of detail." },
    ],
  },
  {
    id: "audits",
    code: "AUDIT",
    category: "compliance",
    title: "Audits & Assessments (PSM / RMP)",
    standard: "OSHA 29 CFR 1910.119(o) / EPA 40 CFR Part 68",
    short:
      "Audits and assessments provide a vital management control for Process Safety Management (PSM), Process Security Management, and Risk Management (RM) Programs.",
    long:
      "We provide formal on-site compliance audits and simpler, less-formal assessments to determine whether individual programs have gaps or deficiencies — each documented in a clear report.",
    items: [
      { t: "Performance of compliance audits", d: "We provide auditors to conduct an on-site PSM and RMP audit and deliver a report documenting the audit." },
      { t: "Performance of assessments", d: "Simpler reviews, less formal than audits, to determine whether individual programs such as mechanical integrity or procedure development have gaps or deficiencies, with a documenting report." },
    ],
  },
  {
    id: "procedure-development",
    code: "PROC",
    category: "compliance",
    title: "Procedure Development",
    standard: "OSHA 29 CFR 1910.119(f)",
    short:
      "Written procedures are required for many activities in a process such as operations and maintenance. Well-designed procedures written for their users prevent costly human error.",
    long:
      "We design new procedures, revise existing ones to current standards, and perform transient operation hazard analysis on critical procedures — always delivered in an appropriate, usable format.",
    items: [
      { t: "Development of new procedures", d: "We design and develop procedures according to established principles and best practices to ensure they are written for their users, provided in an appropriate format." },
      { t: "Revision of existing procedures", d: "We revise existing procedures to meet current standards and advise clients on procedure maintenance, control and training." },
      { t: "Transient operation hazard analysis", d: "We perform transient operation hazard analysis on critical procedures." },
    ],
  },
  {
    id: "psm",
    code: "PSM",
    category: "compliance",
    title: "Process Safety Management (PSM)",
    standard: "OSHA 29 CFR 1910.119",
    short:
      "Companies that handle highly hazardous chemicals in excess of threshold quantities must comply with OSHA's PSM standard, 29 CFR 1910.119, and EPA's Risk Management Program rule, 40 CFR Part 68.",
    long:
      "We build full PSM and Prevention Programs for new facilities, improve existing programs that struggle with implementation, and fine-tune specific elements such as mechanical integrity and management of change.",
    items: [
      { t: "Development of full PSM & Prevention Programs", d: "Typically for new or expanded facilities that come under PSM and/or RMP coverage. We document all program elements and usually assist with implementation." },
      { t: "Improvement of existing programs", d: "We assess the existing program to identify areas for improvement — sometimes a design is sound but implementation is struggling — and improve the weaker parts." },
      { t: "Improvement of specific PSM elements", d: "We help fine-tune mechanical integrity, management of change, and other programs that are part of PSM, with documentation of the work performed." },
    ],
  },
  {
    id: "hazid",
    code: "HAZID",
    category: "risk",
    title: "Hazard Identification (HAZID)",
    standard: "ISO 31000 / API RP 75",
    short:
      "Hazard identification uses checklists of hazards to pinpoint material, system, process, and facility characteristics that could produce undesirable consequences through the occurrence of an incident.",
    long:
      "Our facilitators lead your HAZID team through a structured study and translate the findings into a Hazard Register tailored to your requirements.",
    items: [
      { t: "Performance of HAZID studies", d: "Our facilitators lead your HAZID team in conducting a study and provide a written report of the results." },
      { t: "Development of a Hazard Register", d: "We utilize the results of HAZID studies to prepare a Hazard Register that meets your requirements." },
    ],
  },
  {
    id: "rmp",
    code: "RMP",
    category: "compliance",
    title: "Risk Management Programs (RMP)",
    standard: "EPA 40 CFR Part 68",
    short:
      "Companies that handle certain highly hazardous chemicals in excess of threshold quantities must comply with EPA's Risk Management Program (RMP) rule, 40 CFR Part 68, which embraces OSHA's PSM standard.",
    long:
      "We develop full prevention programs for facilities coming under RMP, improve existing programs, and fine-tune specific elements to keep you compliant and audit-ready.",
    items: [
      { t: "Development of full PSM & Prevention Programs", d: "Typically for new or expanded facilities that come under PSM and/or RMP coverage. We document all elements and usually assist with implementation." },
      { t: "Improvement of existing programs", d: "We assess the existing program to identify areas for improvement and work to improve weaker parts of the program." },
      { t: "Improvement of specific elements", d: "We help companies fine-tune mechanical integrity, management of change, and other programs, with documentation of the work performed." },
    ],
  },
  {
    id: "lopa",
    code: "LOPA",
    category: "risk",
    title: "Layers of Protection Analysis (LOPA)",
    standard: "CCPS LOPA Guidelines / IEC 61511",
    short:
      "LOPA is a simplified risk assessment method. It provides an objective, rational and reproducible way of evaluating the risk of hazard scenarios and comparing it with risk tolerance criteria to decide if safeguards are adequate.",
    long:
      "We develop LOPA programs and technical guidelines, facilitate LOPA studies as part of or separate from PHAs, and help establish risk tolerance criteria suited to your facilities.",
    items: [
      { t: "Development of a LOPA Program", d: "Programs tailored to a company covering the LOPA approach, risk criteria, screening PHA scenarios, and guidance on when to use LOPA. Documentation is provided." },
      { t: "Development of LOPA Guidelines", d: "Technical guidelines addressing failure data, standardizing IPL PFDs, and guidelines for qualifying IPLs. A set of written guidelines is provided." },
      { t: "Facilitation of LOPA studies", d: "We facilitate LOPA studies as part of PHA studies or as separate studies and provide a report documenting the results." },
      { t: "Development of Risk Tolerance Criteria", d: "We assist in developing individual and group risk tolerance criteria suitable for your facilities." },
    ],
  },
  {
    id: "sis",
    code: "SIS",
    category: "systems",
    title: "Safety Instrumented Systems (SIS)",
    standard: "IEC 61511 / ANSI/ISA 84.00.01",
    short:
      "ANSI/ISA-S84.00.01-2003 (IEC 61511 Mod), Application of Safety Instrumented Systems for the Process Industries, addresses the application of Safety Instrumented Systems throughout their lifecycle.",
    long:
      "From SIL determination and Safety Requirement Specifications to FMEDA, SIL verification, and lifecycle assessments, our functional-safety engineers cover the full SIS lifecycle.",
    items: [
      { t: "Determination of required Safety Integrity Levels (SILs)", d: "Using Layers of Protection Analysis (LOPA), risk graphs, and risk matrices. A report documenting the results is provided." },
      { t: "Preparation of Safety Requirements Specifications (SRSs)", d: "We document the requirements for SISs and their SIFs." },
      { t: "Failure Modes, Effects & Diagnostics Analysis (FMEDA)", d: "We determine safe and dangerous, detected and undetected SIF failure rates using component failure data. Results are provided in a report." },
      { t: "Safety Integrity Level (SIL) verification", d: "We determine the SIL values provided by SIFs by calculation, a requirement of IEC 61511 / ISA 84, documented in a report." },
      { t: "SIS lifecycle activities", d: "Assistance with Functional Safety Assessments, e.g. Pre-Startup Safety Reviews, provided in a report." },
      { t: "Procedure development", d: "Procedures for SIS installation, commissioning, testing, operation, maintenance and change reviews that meet prevailing good industry practices." },
      { t: "Modification of PSM Programs to address SISs", d: "We help modify PSM programs to address SIS — process safety information, PHA, operating procedures, mechanical integrity — and update the documentation." },
    ],
  },
  {
    id: "dha",
    code: "DHA",
    category: "dust",
    title: "Dust Hazard Analysis (DHA)",
    standard: "NFPA 652 / NFPA 654",
    short:
      "A Dust Hazard Analysis (DHA) is required by NFPA 652, Standard on the Fundamentals of Combustible Dust. The related NFPA 654 addresses prevention of fire and dust explosions in manufacturing.",
    long:
      "We facilitate all types of DHA studies, mentor your facilitators, review completed studies and provide dedicated scribing, delivering a detailed, comprehensive report.",
    items: [
      { t: "Facilitation of all DHA study types", d: "Design-stage DHAs before a process is built, initial DHAs for operating facilities, and revalidation DHAs (commonly a 5-year cycle) to account for process changes." },
      { t: "Mentoring & coaching your personnel", d: "An experienced facilitator assists your new facilitators in learning the ropes and gaining confidence in facilitating studies." },
      { t: "Reviewing DHAs", d: "Our facilitators review completed DHAs and provide suggestions for improvements useful in revalidating DHAs and improving their quality." },
      { t: "DHA scribing", d: "Our team leaders can simultaneously facilitate and document a study, or provide a separate DHA scribe to handle recording tasks." },
    ],
    note: "We utilize HAZOP-Pro hazard analysis software in conducting and recording studies (other packages can also be used) and provide a detailed, comprehensive report.",
  },
  {
    id: "dhm",
    code: "DHM",
    category: "dust",
    title: "Dust Hazards Management",
    standard: "NFPA 652 Operating Standards",
    short:
      "NFPA 652 requires that a safety management system (SMS) address the hazards of combustible dusts. The management system must address topics such as employee training and operational safeguards.",
    long:
      "We develop new safety management systems, improve existing ones that struggle with implementation, and fine-tune specific elements such as operating procedures, ITM, and management of change.",
    items: [
      { t: "Development of new safety management systems", d: "Typically for new or expanded facilities. We document all program elements and usually assist with implementation." },
      { t: "Improvement of existing safety management systems", d: "We assess the existing system to identify areas for improvement and work to improve weaker parts." },
      { t: "Improvement of specific SMS elements", d: "We help fine-tune operating procedures; inspection, testing and maintenance; and management of change, with documentation of the work performed." },
    ],
  },
  {
    id: "human-factors",
    code: "HF",
    category: "risk",
    title: "Human Factors & Human Failures",
    standard: "CCPS Human Factors Guidelines",
    short:
      "People are key components of industrial processes — involved in design, operation and maintenance. No step in the process life cycle is without some human involvement, making human factors central to safety.",
    long:
      "We address human failures and human factors within PHAs and through dedicated failure and factors studies, plus focused studies on specific issues such as control-center and procedure design.",
    items: [
      { t: "Human failures & factors in PHA", d: "We assist clients in identifying human failures and addressing human factors during PHAs; the worksheets and report document the issues identified." },
      { t: "Human failure analysis studies", d: "A more thorough assessment of human errors, often conducted before a PHA so results can be used in it. A documenting report is provided." },
      { t: "Human factors studies", d: "A thorough assessment of the human factors present in a process that adversely affect human performance, with a documenting report." },
      { t: "Human factors issues", d: "Studies on specific issues such as control-center design, procedures design, competence management and safety culture, with a documenting report." },
    ],
  },
  {
    id: "consequence-modeling",
    code: "CM",
    category: "systems",
    title: "Consequence Modeling",
    standard: "Dispersion & Explosion Modeling",
    short:
      "Consequence modeling determines the impacts of process accidents involving hazardous materials on people, the environment and the process — from release source terms through dispersion and physical effects.",
    long:
      "We quantify releases, model dispersion and physical effects on people and property, and perform sensitivity, importance and uncertainty analysis — each documented in a report.",
    items: [
      { t: "Source term determination", d: "We calculate the amount of hazardous material released in an accident and document the results in a report." },
      { t: "Dispersion analysis", d: "We calculate distances to various concentration endpoints for a released material and the area covered by the plume, documented in a report." },
      { t: "Consequence modeling", d: "We determine the impacts of toxic releases on people on-site and off-site and the environment, and the impacts of fires and explosions on people and property." },
      { t: "Sensitivity, importance & uncertainty analysis", d: "We identify the input variables results are most sensitive to and which are most important, and propagate uncertainties through the analyses." },
    ],
  },
  {
    id: "facility-siting",
    code: "FS",
    category: "systems",
    title: "Facility Siting",
    standard: "API RP 752 / API RP 753 / OSHA PSM",
    short:
      "OSHA's PSM regulations require that facility siting be addressed in Process Hazard Analysis (PHA). Facility siting assesses the possible impacts of fire and explosion on life safety, structures and equipment.",
    long:
      "We develop facility siting programs, incorporate siting into PHAs, perform occupied-building siting studies, and audit current practices against regulatory and best-practice requirements.",
    items: [
      { t: "Development of a Facility Siting program", d: "Advice and guidance tailored to your needs, with a written plan that integrates with your current systems and, if requested, implementation assistance." },
      { t: "PHA studies incorporating Facility Siting", d: "We help ensure your PHAs address facility siting to meet OSHA's expectations; the report documents the approach and the worksheets show the impact on hazard scenarios." },
      { t: "Facility Siting studies", d: "Toxic release, blast over-pressure and thermal radiation footprints are overlaid on the facility plot plan and an occupied building analysis performed, with a report assessing building integrity." },
      { t: "Auditing current siting practices", d: "An audit report against regulatory requirements and/or industry best practices that identifies findings and recommends corrective actions, with implementation help if needed." },
    ],
    note: "We perform studies for new or existing facilities; however, facility siting studies are best performed at the design stage when changes are more easily made.",
  },
  {
    id: "pressure-relief",
    code: "PRV",
    category: "process-eng",
    title: "Pressure Relief System Design & Evaluation",
    standard: "API 520 / 521 / ASME Section VIII",
    short:
      "Design and evaluation of pressure relief systems to protect equipment and personnel from credible overpressure scenarios.",
    long:
      "We identify governing overpressure scenarios, size relief devices, and evaluate relief and disposal systems against API and ASME requirements — delivering defensible documentation for compliance and mechanical integrity.",
    items: [
      { t: "Relief scenario identification", d: "Systematic determination of credible overpressure scenarios and the governing case for each protected system." },
      { t: "Relief device sizing (API 520)", d: "Sizing and selection of relief valves and rupture disks, with PSV datasheets and calculations." },
      { t: "Overpressure protection evaluation (API 521)", d: "Evaluation of relief loads, disposal routing, and adequacy of the overall pressure-relief philosophy." },
      { t: "Relief system documentation", d: "Complete relief study reports, device schedules, and datasheets to support audits and mechanical integrity programs." },
    ],
  },
  {
    id: "alarm-rationalization",
    code: "ALARM",
    category: "process-eng",
    title: "Alarm Rationalization",
    standard: "ISA 18.2 / IEC 62682",
    short:
      "Systematic rationalization of process alarms to reduce alarm floods, nuisance alarms, and operator overload.",
    long:
      "We rationalize alarm systems against a documented alarm philosophy — prioritizing each alarm by consequence and time-to-respond — to improve operator effectiveness and control-room performance.",
    items: [
      { t: "Alarm philosophy development", d: "A documented alarm philosophy establishing definitions, priorities, and design principles per ISA 18.2." },
      { t: "Master alarm database", d: "Creation and documentation of a master alarm database with rationalized setpoints and priorities." },
      { t: "Prioritization & justification", d: "Prioritization of alarms by consequence and required response time, with justification for each setpoint." },
      { t: "Performance benchmarking", d: "Benchmarking of alarm rates and floods, with rationalization workshops to close gaps." },
    ],
  },
  {
    id: "flare-system",
    code: "FLARE",
    category: "process-eng",
    title: "Flare System Design",
    standard: "API 521 / API 537",
    short:
      "Design and hydraulic evaluation of flare and disposal systems for the safe handling of relief and blowdown loads.",
    long:
      "We design and evaluate flare headers, knockout drums, and flare tips — verifying hydraulics, back pressure, thermal radiation, and dispersion for safe, code-compliant disposal of relief loads.",
    items: [
      { t: "Flare header hydraulics", d: "Header network and back-pressure analysis to confirm relief devices perform within design limits." },
      { t: "Flare tip sizing & radiation", d: "Flare tip sizing with thermal radiation and noise evaluation per API 521." },
      { t: "Knockout drum & liquid seal", d: "Design of knockout drums and liquid seals to protect the flare and remove entrained liquids." },
      { t: "Smokeless & purge evaluation", d: "Assessment of smokeless capacity and purge/assist systems for reliable operation." },
    ],
  },
  {
    id: "process-design",
    code: "PED",
    category: "process-eng",
    title: "Process Engineering Design Services",
    standard: "Front-End & Detailed Design",
    short:
      "End-to-end process design deliverables spanning concept, front-end engineering, and detailed design.",
    long:
      "We develop complete process design packages — from PFDs and P&IDs to heat and material balances, equipment sizing, and datasheets — supporting projects from FEED through detailed engineering.",
    items: [
      { t: "PFDs & P&IDs", d: "Development of Process Flow Diagrams and Piping & Instrumentation Diagrams." },
      { t: "Heat & material balances", d: "Rigorous heat and material balances to define process conditions and streams." },
      { t: "Equipment sizing & datasheets", d: "Sizing and process datasheets for pumps, exchangers, vessels, and other equipment." },
      { t: "Line sizing & control philosophy", d: "Line sizing, hydraulics, and development of the process control philosophy." },
    ],
  },
  {
    id: "operating-procedures",
    code: "SOP",
    category: "process-eng",
    title: "Operating Procedure Generation",
    standard: "OSHA 29 CFR 1910.119(f)",
    short:
      "Development of clear, field-ready operating procedures for startup, normal, shutdown, and emergency operations.",
    long:
      "We create operating procedures written for their users — with explicit operating limits, consequences of deviation, and safety steps — and validate them in the field with operators.",
    items: [
      { t: "Startup, normal & shutdown procedures", d: "Step-by-step procedures covering all normal operating modes of the process." },
      { t: "Emergency & abnormal operations", d: "Procedures for emergency shutdowns and abnormal situations to guide safe operator response." },
      { t: "Operating limits & consequences", d: "Documented operating limits and the consequences of deviation for each critical step." },
      { t: "Field verification with operators", d: "Validation of procedures with the operators who use them to ensure clarity and accuracy." },
    ],
  },
  {
    id: "process-simulation",
    code: "SIM",
    category: "process-eng",
    title: "Process Simulation & Modeling Services",
    standard: "Steady-State & Dynamic Simulation",
    short:
      "Steady-state and dynamic process simulation to optimize, debottleneck, and troubleshoot process performance.",
    long:
      "We build and validate process simulation models to evaluate designs, debottleneck units, and support relief and control studies — selecting appropriate thermodynamic methods for reliable results.",
    items: [
      { t: "Steady-state modeling", d: "Steady-state process models for design evaluation and optimization." },
      { t: "Dynamic simulation", d: "Dynamic models supporting control, relief, and depressurization studies." },
      { t: "Debottlenecking & what-if analysis", d: "Case studies to identify constraints and evaluate operational or design changes." },
      { t: "Thermodynamic model validation", d: "Selection and validation of thermodynamic packages against plant or reference data." },
    ],
  },
];

export const STANDARDS = [
  { code: "OSHA 29 CFR 1910.119", label: "Process Safety Management" },
  { code: "EPA 40 CFR Part 68", label: "Risk Management Program" },
  { code: "IEC 61511 / ISA 84", label: "Safety Instrumented Systems" },
  { code: "NFPA 652 / 654", label: "Combustible Dust" },
  { code: "API RP 752 / 753", label: "Facility Siting" },
  { code: "ISO 31000", label: "Risk Management" },
];
